// components/bookingDetails/EditChargesDialog.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CommonButton from 'components/commonButton/CommonButton';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { editDutySlip, getRateCardsV2, clearRateCardsV2 } from 'redux/actions/reservationAction';
import { getDutyType } from 'redux/actions/UserDutyType';

const EditChargesDialog = ({ open, handleClose, tripId, tripDetails }) => {
  const dispatch = useDispatch();

  const editDutySlipLoading = useSelector((state) => state?.editDutySlip?.loading);

  const masterDutyTypeList = useSelector((state) => state?.userDutyType?.dutyTypeList?.data);

  const rateCardsV2List = useSelector((state) => state?.rateCardsV2Reducer?.rateCardsV2List?.data);

  // Form Field States
  const [dutyType, setDutyType] = useState('');
  const [pricingMode, setPricingMode] = useState('systemRate');
  const [rateCardId, setRateCardId] = useState('');
  const [tripStartTime, setTripStartTime] = useState('');
  const [tripEndTime, setTripEndTime] = useState('');
  const [tripStartLocationManual, setTripStartLocationManual] = useState('');
  const [tripEndLocationManual, setTripEndLocationManual] = useState('');
  const [odometerStart, setOdometerStart] = useState(0);
  const [odometerEnd, setOdometerEnd] = useState(0);
  const [distance, setDistance] = useState(0);
  // Billing distance source (2026-09-24): the billed distance is derived from
  // the GPS trail or the odometer readings; ops choose which, never type it.
  const [distanceSource, setDistanceSource] = useState('gps');
  const [gpsDistance, setGpsDistance] = useState(0);
  const [actualTravelTime, setActualTravelTime] = useState(0);
  const [garageToPickup, setGarageToPickup] = useState(0);
  const [dropToGarage, setDropToGarage] = useState(0);
  const [timeIn, setTimeIn] = useState(0);
  const [timeOut, setTimeOut] = useState(0);
  const [extraKm, setExtraKm] = useState(0);
  const [extraTime, setExtraTime] = useState(0);
  const [waitingTime, setWaitingTime] = useState(0);
  const [tollCharges, setTollCharges] = useState(0);
  const [parkingCharges, setParkingCharges] = useState(0);
  const [interstateTaxCharges, setInterstateTaxCharges] = useState(0);
  const [tollAndParkingCharges, setTollAndParkingCharges] = useState(0);
  const [driverAllowance, setDriverAllowance] = useState(0);
  const [nightAllowance, setNightAllowance] = useState(0);
  const [remarks, setRemarks] = useState('');

  // Manual pricing override fields
  const [baseFare, setBaseFare] = useState('');
  const [baseKm, setBaseKm] = useState('');
  const [baseHour, setBaseHour] = useState('');
  const [extraKmRate, setExtraKmRate] = useState('');
  const [extraHourRate, setExtraHourRate] = useState('');
  const [nightAllowanceRate, setNightAllowanceRate] = useState('');
  const [driverAllowanceRate, setDriverAllowanceRate] = useState('');

  const [initialValues, setInitialValues] = useState({});

  // Helper to format date string to YYYY-MM-DDTHH:mm local format
  const formatDateTimeLocal = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60 * 1000);
    return localDate.toISOString().slice(0, 16);
  };

  // Fetch duty types on mount when dialog opens
  useEffect(() => {
    if (open) {
      dispatch(getDutyType());
    }
  }, [open, dispatch]);

  // Fetch rate cards whenever selected duty type changes
  useEffect(() => {
    if (open && tripDetails?.companyId && tripDetails?.city && dutyType && tripDetails?.carTypeId) {
      dispatch(
        getRateCardsV2({
          companyId: tripDetails?.companyId,
          location: tripDetails?.city,
          type: dutyType,
          carTypeId: tripDetails?.carTypeId,
        }),
      );
    }
  }, [open, tripDetails?.companyId, tripDetails?.city, dutyType, tripDetails?.carTypeId, dispatch]);

  // Clean up rate cards when dialog closes
  useEffect(() => {
    return () => {
      dispatch(clearRateCardsV2());
    };
  }, [dispatch]);

  // Populate initial state from tripDetails / dutySlipEditContext when dialog opens
  useEffect(() => {
    if (open && tripDetails) {
      const context = tripDetails?.dutySlipEditContext;
      const pricing = tripDetails?.Payments?.[0]?.tripPricingDetails || {};
      const addInfo = tripDetails?.additionalInfo || {};

      const contextRateConfig = context?.rateConfig || {};
      const tripDetailsRateConfig =
        tripDetails?.rateConfig?.rateConfig || tripDetails?.rateConfig || {};

      const vals = {
        tollCharges: context?.tollCharges ?? pricing?.tollCharges ?? addInfo?.tollCharges ?? 0,
        parkingCharges:
          context?.parkingCharges ?? pricing?.parkingCharges ?? addInfo?.parkingCharges ?? 0,
        interstateTaxCharges:
          context?.interstateTaxCharges ??
          pricing?.interstateTaxCharges ??
          addInfo?.interstateTaxCharges ??
          0,
        tollAndParkingCharges:
          context?.tollAndParkingCharges ??
          pricing?.tollAndParkingCharges ??
          addInfo?.tollAndParkingCharges ??
          0,
        garageToPickup:
          context?.garageToPickup ?? addInfo?.garageToPickup ?? addInfo?.kilometersOut ?? 0,
        dropToGarage: context?.dropToGarage ?? addInfo?.dropToGarage ?? addInfo?.kilometersIn ?? 0,
        remarks: context?.remarks ?? context?.note ?? addInfo?.remarks ?? addInfo?.note ?? '',
        tripStartLocationManual:
          context?.tripStartLocationManual ??
          context?.tripStartLocation ??
          addInfo?.tripStartLocationManual ??
          tripDetails?.tripStartLocation ??
          '',
        tripEndLocationManual:
          context?.tripEndLocationManual ??
          context?.tripEndLocation ??
          addInfo?.tripEndLocationManual ??
          tripDetails?.tripEndLocation ??
          '',
        distance: context?.distance ?? tripDetails?.actualDistance ?? 0,
        distanceSource: context?.distanceSource ?? addInfo?.distanceSource ?? 'gps',
        gpsDistance:
          context?.gpsDistance ??
          addInfo?.gpsDistance ??
          context?.distance ??
          tripDetails?.distance ??
          0,
        actualTravelTime: context?.actualTravelTime ?? tripDetails?.actualTravelTime ?? 0,
        driverAllowance:
          context?.driverAllowance ?? pricing?.driverAllowance ?? addInfo?.driverAllowance ?? 0,
        nightAllowance:
          context?.nightAllowance ?? pricing?.nightAllowance ?? addInfo?.nightAllowance ?? 0,

        extraKm:
          context?.extraKm ?? tripDetails?.extraKm ?? pricing?.extraKm ?? addInfo?.extraKm ?? 0,
        extraTime:
          context?.extraTime ??
          tripDetails?.extraTime ??
          pricing?.extraTime ??
          addInfo?.extraTime ??
          0,
        timeIn: context?.timeIn ?? tripDetails?.timeIn ?? pricing?.timeIn ?? addInfo?.timeIn ?? 0,
        timeOut:
          context?.timeOut ?? tripDetails?.timeOut ?? pricing?.timeOut ?? addInfo?.timeOut ?? 0,
        waitingTime:
          context?.waitingTime ??
          tripDetails?.waitingTime ??
          pricing?.waitingTime ??
          addInfo?.waitingTime ??
          0,
        odometerStart:
          context?.odometerStart ??
          tripDetails?.odometerStart ??
          pricing?.odometerStart ??
          addInfo?.odometerStart ??
          0,
        odometerEnd:
          context?.odometerEnd ??
          tripDetails?.odometerEnd ??
          pricing?.odometerEnd ??
          addInfo?.odometerEnd ??
          0,

        dutyType: context?.dutyType ?? tripDetails?.type ?? '',
        rateCardId: context?.rateCardId ?? addInfo?.sourceRateCardId?.[0] ?? '',
        pricingMode: context?.pricingMode ?? addInfo?.pricingMode ?? 'systemRate',
        tripStartTime: context?.tripStartTime ?? tripDetails?.scheduledTime ?? '',
        tripEndTime:
          context?.tripEndTime ?? tripDetails?.completedAt ?? tripDetails?.tripEndTime ?? '',

        // rateConfig Manual Overrides
        baseFare:
          contextRateConfig?.cost ??
          tripDetailsRateConfig?.cost ??
          tripDetails?.rateConfig?.baseRate ??
          '',
        baseKm: contextRateConfig?.maxDistanceKms ?? tripDetailsRateConfig?.maxDistanceKms ?? '',
        baseHour: contextRateConfig?.duration ?? tripDetailsRateConfig?.duration ?? '',
        extraKmRate: contextRateConfig?.extraKM?.rate ?? tripDetailsRateConfig?.extraKM?.rate ?? '',
        extraHourRate:
          contextRateConfig?.extraHrs?.rate ?? tripDetailsRateConfig?.extraHrs?.rate ?? '',
        nightAllowanceRate:
          contextRateConfig?.nightAllowance?.rate ??
          tripDetailsRateConfig?.nightAllowance?.rate ??
          '',
        driverAllowanceRate:
          contextRateConfig?.driverAllowance?.rate ??
          tripDetailsRateConfig?.driverAllowance?.rate ??
          '',
      };

      setTollCharges(vals.tollCharges);
      setParkingCharges(vals.parkingCharges);
      setInterstateTaxCharges(vals.interstateTaxCharges);
      setTollAndParkingCharges(vals.tollAndParkingCharges);
      setGarageToPickup(vals.garageToPickup);
      setDropToGarage(vals.dropToGarage);
      setRemarks(vals.remarks);
      setTripStartLocationManual(vals.tripStartLocationManual);
      setTripEndLocationManual(vals.tripEndLocationManual);
      setDistance(vals.distance);
      setDistanceSource(vals.distanceSource);
      setGpsDistance(vals.gpsDistance);
      setActualTravelTime(vals.actualTravelTime);
      setDriverAllowance(vals.driverAllowance);
      setNightAllowance(vals.nightAllowance);

      setExtraKm(vals.extraKm);
      setExtraTime(vals.extraTime);
      setTimeIn(vals.timeIn);
      setTimeOut(vals.timeOut);
      setWaitingTime(vals.waitingTime);
      setOdometerStart(vals.odometerStart);
      setOdometerEnd(vals.odometerEnd);

      setDutyType(vals.dutyType);
      setPricingMode(vals.pricingMode);
      setRateCardId(vals.rateCardId);
      setTripStartTime(vals.tripStartTime ? formatDateTimeLocal(vals.tripStartTime) : '');
      setTripEndTime(vals.tripEndTime ? formatDateTimeLocal(vals.tripEndTime) : '');

      // Overrides
      setBaseFare(vals.baseFare);
      setBaseKm(vals.baseKm);
      setBaseHour(vals.baseHour);
      setExtraKmRate(vals.extraKmRate);
      setExtraHourRate(vals.extraHourRate);
      setNightAllowanceRate(vals.nightAllowanceRate);
      setDriverAllowanceRate(vals.driverAllowanceRate);

      setInitialValues(vals);
    }
  }, [open, tripDetails]);

  // Dropdown option maps
  const dutyTypeOptions = (Array.isArray(masterDutyTypeList) ? masterDutyTypeList : [])?.map(
    (val) => ({
      value: val?.dutyTypeId ?? val?.id,
      label: val?.name,
    }),
  );

  const rateCardOptions = (Array.isArray(rateCardsV2List) ? rateCardsV2List : [])?.map((val) => ({
    value: val?.id,
    label:
      val?.rateConfig?.name ||
      `${val?.rateConfig?.duration || 0}hr - ${val?.rateConfig?.maxDistanceKms || 0}km`,
  }));

  const hasPredefinedRateCards = Array.isArray(rateCardsV2List) && rateCardsV2List.length > 0;

  // Odometer distance from the readings as currently typed; the billed
  // distance shown follows the selected source. Both are display-only here.
  const liveOdometerDistance =
    Number(odometerStart) > 0 &&
    Number(odometerEnd) > 0 &&
    Number(odometerEnd) >= Number(odometerStart)
      ? Number((Number(odometerEnd) - Number(odometerStart)).toFixed(2))
      : null;
  const billedDistance =
    distanceSource === 'odometer' && liveOdometerDistance != null
      ? liveOdometerDistance
      : gpsDistance;

  // If rateCardsV2List was fetched and is empty, automatically force manual override mode (Case B)
  useEffect(() => {
    if (distanceSource === 'odometer' && liveOdometerDistance == null) {
      setDistanceSource('gps');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveOdometerDistance]);

  useEffect(() => {
    if (rateCardsV2List && rateCardsV2List.length === 0) {
      setPricingMode('overriddenRate');
    }
  }, [rateCardsV2List]);

  // Autofill rates when rateCardId is changed
  useEffect(() => {
    if (rateCardId && rateCardsV2List) {
      const isInitialRateCard = rateCardId === initialValues.rateCardId;
      if (!isInitialRateCard) {
        const selected = rateCardsV2List.find((rc) => rc.id === rateCardId);
        if (selected) {
          const rc = selected.rateConfig || {};
          setBaseFare(rc.cost ?? '');
          setBaseKm(rc.maxDistanceKms ?? '');
          setBaseHour(rc.duration ?? '');
          setExtraKmRate(rc.extraKM?.rate ?? '');
          setExtraHourRate(rc.extraHrs?.rate ?? '');
          setNightAllowanceRate(rc.nightAllowance?.rate ?? '');
          setDriverAllowanceRate(rc.driverAllowance?.rate ?? '');
        }
      }
    }
  }, [rateCardId, rateCardsV2List, initialValues.rateCardId]);

  const handleSave = () => {
    // Audit Note validation
    if (!remarks || !remarks.trim()) {
      toast.error('Correction Remarks/Reason is required');
      return;
    }

    // Time validation
    if (tripStartTime && tripEndTime) {
      if (new Date(tripEndTime) <= new Date(tripStartTime)) {
        toast.error('Trip End Time must be after Trip Start Time');
        return;
      }
    }

    // Odometer validation
    if (odometerStart !== undefined && odometerEnd !== undefined) {
      if (Number(odometerEnd) < Number(odometerStart)) {
        toast.error('End Odometer must be greater than or equal to Start Odometer');
        return;
      }
    }

    // Duty type validation
    const dutyTypeChanged = Number(dutyType) !== Number(initialValues.dutyType);
    if (dutyTypeChanged) {
      if (pricingMode === 'systemRate' && !rateCardId) {
        toast.error('Please select a Rate Card for the new duty type');
        return;
      }
      if (pricingMode === 'overriddenRate' && (!baseFare || !baseKm || !baseHour)) {
        toast.error('Please fill manual pricing configuration rates');
        return;
      }
    }

    // Build payload with ONLY changed fields
    const payload = {};

    const checkAndSetNumber = (val, key) => {
      if (Number(val) !== Number(initialValues[key] ?? 0)) {
        payload[key] = Number(val) || 0;
      }
    };

    const checkAndSetString = (val, key) => {
      const trimmedVal = (val ?? '').toString().trim();
      const trimmedInit = (initialValues[key] ?? '').toString().trim();
      if (trimmedVal !== trimmedInit) {
        payload[key] = trimmedVal;
      }
    };

    // The time pickers hold minutes while the stored instants hold seconds (the
    // OTP moments). Comparing full ISO strings re-sent both times on every save
    // and stripped the seconds server-side. Compare at the picker's resolution.
    const toMinuteIso = (d) => {
      const t = new Date(d).getTime();
      if (Number.isNaN(t)) return '';
      return new Date(Math.floor(t / 60000) * 60000).toISOString();
    };
    const checkAndSetDate = (val, key) => {
      if (!val) return;
      const initMinute = initialValues[key] ? toMinuteIso(initialValues[key]) : '';
      if (toMinuteIso(val) !== initMinute) {
        payload[key] = new Date(val).toISOString();
      }
    };

    checkAndSetNumber(tollCharges, 'tollCharges');
    checkAndSetNumber(parkingCharges, 'parkingCharges');
    checkAndSetNumber(interstateTaxCharges, 'interstateTaxCharges');
    checkAndSetNumber(tollAndParkingCharges, 'tollAndParkingCharges');
    checkAndSetNumber(garageToPickup, 'garageToPickup');
    checkAndSetNumber(dropToGarage, 'dropToGarage');
    checkAndSetString(remarks, 'remarks');
    checkAndSetString(tripStartLocationManual, 'tripStartLocationManual');
    checkAndSetString(tripEndLocationManual, 'tripEndLocationManual');
    // distance is derived server-side; only the source is sent.
    if (distanceSource !== (initialValues.distanceSource ?? 'gps')) {
      payload.distanceSource = distanceSource;
    }
    checkAndSetNumber(actualTravelTime, 'actualTravelTime');
    checkAndSetNumber(driverAllowance, 'driverAllowance');
    checkAndSetNumber(nightAllowance, 'nightAllowance');

    checkAndSetNumber(extraKm, 'extraKm');
    checkAndSetNumber(extraTime, 'extraTime');
    checkAndSetNumber(timeIn, 'timeIn');
    checkAndSetNumber(timeOut, 'timeOut');
    checkAndSetNumber(waitingTime, 'waitingTime');
    checkAndSetNumber(odometerStart, 'odometerStart');
    checkAndSetNumber(odometerEnd, 'odometerEnd');

    checkAndSetNumber(dutyType, 'dutyType');

    if (pricingMode === 'systemRate') {
      // System Rate Card always names the card, even when it is the one the
      // trip already points at: the backend re-fetches a card only when an
      // id arrives, and re-selecting the card is the agreed way to refresh
      // (or repair) the trip's copy of it (ops rule, 2026-09-24).
      if (rateCardId) {
        payload.rateCardId = rateCardId;
      }
    } else {
      if (rateCardId) {
        checkAndSetString(rateCardId, 'rateCardId');
      }
    }

    checkAndSetDate(tripStartTime, 'tripStartTime');
    checkAndSetDate(tripEndTime, 'tripEndTime');

    if (pricingMode === 'overriddenRate') {
      const rcChanged =
        Number(baseFare) !== Number(initialValues.baseFare ?? 0) ||
        Number(baseKm) !== Number(initialValues.baseKm ?? 0) ||
        Number(baseHour) !== Number(initialValues.baseHour ?? 0) ||
        Number(extraKmRate) !== Number(initialValues.extraKmRate ?? 0) ||
        Number(extraHourRate) !== Number(initialValues.extraHourRate ?? 0) ||
        Number(nightAllowanceRate) !== Number(initialValues.nightAllowanceRate ?? 0) ||
        Number(driverAllowanceRate) !== Number(initialValues.driverAllowanceRate ?? 0);

      if (rcChanged || initialValues.pricingMode !== 'overriddenRate' || !hasPredefinedRateCards) {
        payload.rateConfig = {
          cost: Number(baseFare) || 0,
          duration: Number(baseHour) || 0,
          durationType: 'hour',
          maxDistanceKms: Number(baseKm) || 0,
          extraKM: {
            rate: Number(extraKmRate) || 0,
          },
          extraHrs: {
            rate: Number(extraHourRate) || 0,
          },
          nightAllowance: {
            rate: Number(nightAllowanceRate) || 0,
          },
          driverAllowance: {
            rate: Number(driverAllowanceRate) || 0,
          },
        };
      }
    }

    if (Object.keys(payload).length === 0) {
      toast.info('No changes to update');
      return;
    }

    // Dispatch to Redux - Saga handles API call, toast, and modal close
    dispatch(editDutySlip(payload, tripId, handleClose));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle className="flex justifyBetween alignCenter">
        <Typography variant="head5" className="fw600 colorBlack">
          Edit Duty Slip
        </Typography>
        <IconButton onClick={handleClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2.5}>
          {/* Section 1: General Settings */}
          <Grid item xs={12}>
            <Typography variant="body2" className="fw600 colorPrimary">
              General Configuration
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Duty Type"
              fullWidth
              size="small"
              value={dutyType}
              onChange={(e) => setDutyType(e.target.value)}
            >
              {dutyTypeOptions?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Pricing Mode"
              fullWidth
              size="small"
              value={pricingMode}
              onChange={(e) => setPricingMode(e.target.value)}
              disabled={!hasPredefinedRateCards}
            >
              <MenuItem value="systemRate">System Rate Card</MenuItem>
              <MenuItem value="overriddenRate">Manual Pricing Override</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            {pricingMode === 'systemRate' ? (
              <TextField
                select
                label="Rate Card"
                fullWidth
                size="small"
                value={rateCardId}
                onChange={(e) => setRateCardId(e.target.value)}
                disabled={!rateCardOptions || rateCardOptions.length === 0}
              >
                {rateCardOptions?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Box sx={{ minHeight: '40px' }} />
            )}
          </Grid>

          {/* Section 2: Manual Override Settings */}
          {pricingMode === 'overriddenRate' && (
            <>
              <Grid item xs={12}>
                <Typography variant="body2" className="fw600 colorPrimary pt10">
                  Manual Pricing Override Configuration
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Base Fare"
                  type="number"
                  fullWidth
                  size="small"
                  value={baseFare}
                  onChange={(e) => setBaseFare(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Base Distance"
                  type="number"
                  fullWidth
                  size="small"
                  value={baseKm}
                  onChange={(e) => setBaseKm(e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">km</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Base Time"
                  type="number"
                  fullWidth
                  size="small"
                  value={baseHour}
                  onChange={(e) => setBaseHour(e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">hr</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Extra KM Rate"
                  type="number"
                  fullWidth
                  size="small"
                  value={extraKmRate}
                  onChange={(e) => setExtraKmRate(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Extra Hour Rate"
                  type="number"
                  fullWidth
                  size="small"
                  value={extraHourRate}
                  onChange={(e) => setExtraHourRate(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Night Allowance"
                  type="number"
                  fullWidth
                  size="small"
                  value={nightAllowanceRate}
                  onChange={(e) => setNightAllowanceRate(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Driver Allowance"
                  type="number"
                  fullWidth
                  size="small"
                  value={driverAllowanceRate}
                  onChange={(e) => setDriverAllowanceRate(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>
            </>
          )}

          {/* Section 3: Schedule & Locations */}
          <Grid item xs={12}>
            <Typography variant="body2" className="fw600 colorPrimary pt10">
              Schedule & Locations
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Trip Start Time"
              type="datetime-local"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={tripStartTime}
              onChange={(e) => setTripStartTime(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Trip End Time"
              type="datetime-local"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={tripEndTime}
              onChange={(e) => setTripEndTime(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Trip Start Location"
              type="text"
              fullWidth
              size="small"
              value={tripStartLocationManual}
              onChange={(e) => setTripStartLocationManual(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Trip End Location"
              type="text"
              fullWidth
              size="small"
              value={tripEndLocationManual}
              onChange={(e) => setTripEndLocationManual(e.target.value)}
            />
          </Grid>

          {/* Section 4: Distance, Readings & Duration */}
          <Grid item xs={12}>
            <Typography variant="body2" className="fw600 colorPrimary pt10">
              Odometer, Distance & Duration
            </Typography>
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Start Odometer"
              type="number"
              fullWidth
              size="small"
              value={odometerStart}
              onChange={(e) => setOdometerStart(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">km</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="End Odometer"
              type="number"
              fullWidth
              size="small"
              value={odometerEnd}
              onChange={(e) => setOdometerEnd(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">km</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Distance"
              type="number"
              fullWidth
              size="small"
              value={billedDistance}
              helperText={
                distanceSource === 'odometer'
                  ? 'from the odometer readings'
                  : `GPS trail${liveOdometerDistance != null ? `, odometer ${liveOdometerDistance} km` : ''}`
              }
              InputProps={{
                readOnly: true,
                endAdornment: <InputAdornment position="end">km</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Actual Travel Time"
              type="number"
              fullWidth
              size="small"
              value={actualTravelTime}
              onChange={(e) => setActualTravelTime(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">hr</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormLabel sx={{ fontSize: 12, mr: 2 }}>Bill on</FormLabel>
            <RadioGroup
              row
              value={distanceSource}
              onChange={(e) => setDistanceSource(e.target.value)}
              sx={{ display: 'inline-flex' }}
            >
              <FormControlLabel value="gps" control={<Radio size="small" />} label="GPS distance" />
              <FormControlLabel
                value="odometer"
                control={<Radio size="small" />}
                label="Odometer distance"
                disabled={liveOdometerDistance == null}
              />
            </RadioGroup>
            {liveOdometerDistance == null && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                enter both odometer readings to bill on the odometer
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Garage to Pickup"
              type="number"
              fullWidth
              size="small"
              value={garageToPickup}
              onChange={(e) => setGarageToPickup(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">km</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Drop to Garage"
              type="number"
              fullWidth
              size="small"
              value={dropToGarage}
              onChange={(e) => setDropToGarage(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">km</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Time In"
              type="number"
              fullWidth
              size="small"
              value={timeIn}
              onChange={(e) => setTimeIn(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">min</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Time Out"
              type="number"
              fullWidth
              size="small"
              value={timeOut}
              onChange={(e) => setTimeOut(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">min</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Extra KM"
              type="number"
              fullWidth
              size="small"
              value={extraKm}
              onChange={(e) => setExtraKm(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">km</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Extra Time"
              type="number"
              fullWidth
              size="small"
              value={extraTime}
              onChange={(e) => setExtraTime(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">min</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Waiting Time"
              type="number"
              fullWidth
              size="small"
              value={waitingTime}
              onChange={(e) => setWaitingTime(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">min</InputAdornment>,
              }}
            />
          </Grid>

          {/* Section 5: Charges & Allowances */}
          <Grid item xs={12}>
            <Typography variant="body2" className="fw600 colorPrimary pt10">
              Charges & Allowances
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Tolls"
              type="number"
              fullWidth
              size="small"
              value={tollCharges}
              onChange={(e) => setTollCharges(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Parking Charges"
              type="number"
              fullWidth
              size="small"
              value={parkingCharges}
              onChange={(e) => setParkingCharges(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Interstate Tax Charges"
              type="number"
              fullWidth
              size="small"
              value={interstateTaxCharges}
              onChange={(e) => setInterstateTaxCharges(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Driver Allowance"
              type="number"
              fullWidth
              size="small"
              value={driverAllowance}
              onChange={(e) => setDriverAllowance(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Night Allowance"
              type="number"
              fullWidth
              size="small"
              value={nightAllowance}
              onChange={(e) => setNightAllowance(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Toll & Parking Charges"
              type="number"
              fullWidth
              size="small"
              value={tollAndParkingCharges}
              onChange={(e) => setTollAndParkingCharges(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>

          {/* Section 6: Audit Control */}
          <Grid item xs={12}>
            <Typography variant="body2" className="fw600 colorPrimary pt10">
              Audit Control
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Correction Remarks (Required) *"
              multiline
              rows={2}
              fullWidth
              size="small"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Explain why this duty slip is manually corrected"
            />
          </Grid>
        </Grid>

        <Grid container justifyContent="flex-end" className="mt20">
          <CommonButton
            name={editDutySlipLoading ? 'Updating...' : 'Update'}
            btnClass="btnSm"
            onClick={handleSave}
            disabled={editDutySlipLoading}
          />
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EditChargesDialog;
